import {Component, Input, Output, EventEmitter, OnInit} from '@angular/core';
import {EventsService} from '../../../../../../../../@shared/services/events-service';
import {EventClassResponse, EventResponse} from '../../../../../../../../@shared/models/response.module';
import {_AQH_EVE_10, _AQH_EVE_9, AQH_EVE_6} from '../../../../../../../../@shared/models/input.module';
import {EVENT_CLASS_STATUSES, formatTimeDMYHHMM} from '../../../../../../../../@core/utils/utils.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {AddClass} from './components/add-class/add-class';
import {EditClass} from './components/edit-class/edit-class';
import {CopyClassModal} from './components/copy-class-modal/copy-class-modal';
import {MembershipPricingModal} from './components/membership-pricing-modal/membership-pricing-modal';
import {ScoringConfigModal} from './components/scoring-config-modal/scoring-config-modal';
import {RoundsConfigModal} from './components/rounds-config-modal/rounds-config-modal';
import {DeleteModal} from '../../../../../../../../@shared/components/delete-modal/delete-modal';
import {ViewClassRegistrations} from './components/view-class-registrations/view-class-registrations';
import {TranslateService} from '@ngx-translate/core';
import {ToastService} from '../../../../../../../../@core/services/toast.service';

interface EventDay {
  date: Date;
  label: string;
  shortLabel: string;
  isToday: boolean;
}

@Component({
  selector: 'app-manage-event-classes',
  standalone: false,
  templateUrl: './manage-event-classes.html',
  styleUrl: './manage-event-classes.css'
})
export class ManageEventClasses implements OnInit {
  @Input() event: EventResponse
  @Output() dataChanged = new EventEmitter<void>();
  eventClasses: EventClassResponse[]
  filteredClasses: EventClassResponse[]
  loading: boolean = false;

  // Day tabs
  eventDays: EventDay[] = [];
  selectedDay: Date | null = null;

  constructor(
    private eventService: EventsService,
    private modalService: NgbModal,
    private translateService: TranslateService,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    this.generateEventDays();
    this.getEventClasses();
  }

  generateEventDays() {
    if (!this.event.start_date || !this.event.end_date) {
      return;
    }

    const startDate = new Date(this.event.start_date);
    const endDate = new Date(this.event.end_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    this.eventDays = [];
    const currentDate = new Date(startDate);
    currentDate.setHours(0, 0, 0, 0);

    while (currentDate <= endDate) {
      const dayDate = new Date(currentDate);
      const isToday = dayDate.getTime() === today.getTime();

      this.eventDays.push({
        date: dayDate,
        label: this.formatDayLabel(dayDate),
        shortLabel: this.formatShortDayLabel(dayDate),
        isToday: isToday
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Select first day by default, or today if it's within the event dates
    if (this.eventDays.length > 0) {
      const todayTab = this.eventDays.find(d => d.isToday);
      this.selectedDay = todayTab ? todayTab.date : this.eventDays[0].date;
    }
  }

  formatDayLabel(date: Date): string {
    const weekday = date.toLocaleDateString('en-GB', { weekday: 'long' }).toLowerCase();
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    return `${weekday} - ${day}.${month}.${year}`;
  }

  formatShortDayLabel(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${day}.${month}`;
  }

  selectDay(day: EventDay) {
    this.selectedDay = day.date;
    this.filterClassesByDay();
  }

  filterClassesByDay() {
    if (!this.eventClasses || !this.selectedDay) {
      this.filteredClasses = this.eventClasses || [];
      return;
    }

    this.filteredClasses = this.eventClasses.filter(eventClass => {
      if (!eventClass.start_date_time) return false;
      const classDate = new Date(eventClass.start_date_time);
      return this.isSameDay(classDate, this.selectedDay!);
    });
  }

  isSameDay(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  }

  getClassCountForDay(day: EventDay): number {
    if (!this.eventClasses) return 0;
    return this.eventClasses.filter(eventClass => {
      if (!eventClass.start_date_time) return false;
      const classDate = new Date(eventClass.start_date_time);
      return this.isSameDay(classDate, day.date);
    }).length;
  }

  getEventClasses() {
    this.loading = true;
    let params: AQH_EVE_6 = {
      event_id: this.event.event_id
    }
    this.eventService.getEventClasses(params).subscribe({
      next: (data) => {
        this.eventClasses = data;
        this.filterClassesByDay();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  getTotalRegistrations() {
    let registrations = 0;

    for (let eventClass of this.eventClasses) {
      registrations += eventClass.event_class_registrations.length;
    }
    return registrations
  }

  openAddEventClassModal() {
    let modalRef = this.modalService.open(AddClass, { size: 'lg', centered: true });
    modalRef.componentInstance.event = this.event;
    modalRef.componentInstance.selectedDate = this.selectedDay;
    modalRef.result.then((res) => {
      if (res){
        this.getEventClasses();
        this.dataChanged.emit();
      }
    })
  }

  openEditEventClassModal(eventClass: EventClassResponse) {
    let modalRef = this.modalService.open(EditClass, { size: 'lg', centered: true });
    modalRef.componentInstance.eventClass = eventClass;
    modalRef.result.then((res) => {
      if (res){
        this.getEventClasses();
        this.dataChanged.emit();
      }
    })
  }

  openCopyClassModal(eventClass: EventClassResponse) {
    let modalRef = this.modalService.open(CopyClassModal, { size: 'md', centered: true });
    modalRef.componentInstance.eventClass = eventClass;
    modalRef.componentInstance.event = this.event;
    modalRef.result.then((res) => {
      if (res) {
        this.getEventClasses();
        this.dataChanged.emit();
      }
    })
  }

  openViewRegistrationsModal(eventClass: EventClassResponse) {
    let modalRef = this.modalService.open(ViewClassRegistrations, { size: 'xl', centered: true });
    modalRef.componentInstance.eventClass = eventClass;
    modalRef.componentInstance.companyId = this.event.company_id;
    modalRef.componentInstance.company = this.event.company;

    modalRef.result.then((updated) => {
      if (updated) {
        // Refresh the event classes list to show updated data
        this.getEventClasses();
      }
    }, () => {
      // Modal dismissed without changes
    });
  }

  openMembershipPricingModal(eventClass: EventClassResponse) {
    let modalRef = this.modalService.open(MembershipPricingModal, { size: 'lg', centered: true });
    modalRef.componentInstance.itemType = 'class';
    modalRef.componentInstance.itemId = eventClass.event_class_id;
    modalRef.componentInstance.itemName = eventClass.class_name;
    modalRef.componentInstance.basePrice = eventClass.price || 0;
    modalRef.componentInstance.useCents = false;
    modalRef.componentInstance.currency = this.event?.default_currency || eventClass.price_currency || 'NOK';

    modalRef.result.then((res) => {
      if (res) {
        this.getEventClasses();
      }
    }, () => {});
  }

  openScoringConfigModal(eventClass: EventClassResponse) {
    let modalRef = this.modalService.open(ScoringConfigModal, { size: 'md', centered: true });
    modalRef.componentInstance.eventClass = eventClass;
    modalRef.result.then((res) => {
      if (res) {
        this.getEventClasses();
      }
    }, () => {});
  }

  openRoundsConfigModal(eventClass: EventClassResponse) {
    let modalRef = this.modalService.open(RoundsConfigModal, { size: 'md', centered: true });
    modalRef.componentInstance.eventClass = eventClass;
    modalRef.result.then((res) => {
      if (res) {
        this.getEventClasses();
      }
    }, () => {});
  }

  deleteEventClass(eventClass: EventClassResponse) {
    let modalRef = this.modalService.open(DeleteModal, { size: 'md', centered: true });
    modalRef.componentInstance.description = this.translateService.instant('manageEvent.classes.deleteConfirm');
    modalRef.result.then((res) => {
      if (res){
        let params: _AQH_EVE_9 = {
          event_id: this.event.event_id,
          event_class_id: eventClass.event_class_id
        }
        this.eventService.deleteEventClass(params).subscribe({
          next: () => {
            this.toastService.success(this.translateService.instant('manageEvent.classes.deleteSuccess'));
            this.getEventClasses();
            this.dataChanged.emit();
          },
          error: () => {
            this.toastService.error(this.translateService.instant('manageEvent.classes.deleteError'));
          }
        });
      }
    })
  }

  updateEventClassStatus(eventClass: EventClassResponse, newStatus: number) {
    let params: _AQH_EVE_10 = {
      event_class_id: eventClass.event_class_id,
      event_class_status_id: newStatus
    }
    this.eventService.editEventClassStatus(params).subscribe({
      next: () => {
        this.toastService.success(this.translateService.instant('manageEvent.classes.statusUpdateSuccess'));
        this.getEventClasses();
      },
      error: () => {
        this.toastService.error(this.translateService.instant('manageEvent.classes.statusUpdateError'));
      }
    });
  }

  protected readonly formatTimeDMYHHMM = formatTimeDMYHHMM;
  protected readonly EVENT_CLASS_STATUSES = EVENT_CLASS_STATUSES;
}
